import React, { useEffect, useState } from "react";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import { CurrentLoginPage, Severity } from "node-library/dist/constants";
import { useNavigate } from "react-router-dom";

import { FrontendCompany } from "node-library/dist/portal";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import {
	CustomCheckbox,
	CustomTextInput,
} from "../../common-components/inputs";
import { BlueButton } from "../../common-components/button";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { validateEmail } from "../../helpers/inputHelper";
import { YellowWarnIcon } from "../../common-components/toast/warnIcons";
import { ButtonTextLoadingAnimation } from "../../common-components/animation/ButtonLoadingAnimation";

interface Props {
	company: FrontendCompany;
	setCurrentPage: React.Dispatch<React.SetStateAction<CurrentLoginPage>>;
}

export default function LoginPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const navigate = useNavigate();
	const company = props.company;

	const [email, setEmail] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [rememberMe, setRememberMe] = useState(false);

	const [loggingIn, setLoggingIn] = useState<boolean>(false);

	const [error, setError] = useState("");
	const [emailError, setEmailError] = useState<string>("");
	const [passwordError, setPasswordError] = useState<string>("");

	const [isDisabled, setIsDisabled] = useState(false);

	const [timer, setTimer] = useState<number>(0); // To track the remaining time for the countdown

	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (isDisabled && timer > 0) {
			interval = setInterval(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
		} else if (timer <= 0) {
			setIsDisabled(false); // Re-enable the button once the timer is finished
		}

		return () => clearInterval(interval); // Clean up the interval on component unmount or when timer reaches 0
	}, [isDisabled, timer]);

	const handleSubmit = async () => {
		try {
			if (!email) {
				setEmailError(translation("MissingInformation"));
				return;
			}
			if (!validateEmail(email)) {
				setEmailError(translation("InvalidEmail"));
				return;
			}
			if (!password) {
				setPasswordError(translation("MissingInformation"));
				return;
			}
			setEmailError(""); // Clear error
			setPasswordError("");
			// Simulate login (replace this with your authentication logic)
			setLoggingIn(true);
			const data = await sendPOSTRequest("/logins/login-with-password", {
				userName: email,
				password,
				rememberMe,
			});
			if (data?.success) {
				if (data?.userInfo) {
					localStorage.setItem(
						"billionspectra_userInfo",
						JSON.stringify(data.userInfo)
					);
				}

				navigate("/portal/view-order");
			}
		} catch (err: any) {
			if (err.response.data === "Invalid credentials!") {
				openSnackbar(
					translation("InvalidCredentials"),
					Severity.ERROR,
					translation("InvalidCredentialsDescription"),
					<RedErrorIcon />
				);
			} else if (err.response.data === "Registration not approved!") {
				openSnackbar(
					translation("RegistrationPendingApproval"),
					Severity.WARN,
					translation("RegistrationPendingApprovalDescription"),
					<YellowWarnIcon />
				);
			} else if (err.response?.status === 429) {
				const retryAfter =
					err.response.headers["retry-after"] ||
					err.response.headers["Retry-After"];

				const waitTime = parseInt(retryAfter, 10) || 60; // Fallback to 60 seconds if no retry-after header is present

				setTimer(waitTime); // Set the countdown timer
				setIsDisabled(true); // Disable the button
				openSnackbar(
					translation("TooManyRequest"),
					Severity.WARN,
					translation("TooManyRequestDescription"),
					<YellowWarnIcon />
				);
			} else {
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />
				);
			}
		} finally {
			setLoggingIn(false);
		}
	};

	useEffect(() => {
		const checkToken = async () => {
			try {
				const response = await sendGETRequest("/logins/validate-token", {});
				if (response.success) {
					// Token is valid, redirect to landing page
					navigate("/portal/view-order");
				}
			} catch (err) {
				//no-op
			}
		};

		checkToken();
	}, [navigate]);

	return (
		<div className="login-container">
			<div className="typography-heading-01 login-heading-01">
				<div>{translation("WelcomeTo")}</div>
				<div>{company?.companyName}</div>
			</div>
			<div className="typography-body-02" style={{ marginBottom: "10px" }}>
				<span>{`${translation("LoginInstruction")} `}</span>
			</div>
			<div>
				<CustomTextInput
					id={`login-email-input`}
					value={email}
					setValue={(value) => {
						setEmail(value);
					}}
					label={translation("Email")}
					required
					errorMsg={emailError}
					setErrorMsg={setEmailError}
				/>
			</div>
			<div>
				<CustomTextInput
					id={`login-password-input`}
					value={password}
					setValue={(value) => {
						setPassword(value);
					}}
					label={translation("Password")}
					type="password"
					required
					errorMsg={passwordError}
					setErrorMsg={setPasswordError}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							handleSubmit();
						}
					}}
				/>
			</div>
			<div className="login-bottom-options-container">
				<CustomCheckbox
					className={`remember-me-checkbox`}
					label={translation("RememberMe")}
					labelClassName={"typography-body-02"}
					checked={rememberMe}
					setChecked={() => {
						setRememberMe(!rememberMe);
					}}
				/>
				<div
					className="typography-body-02"
					style={{
						color: NeutralColor.NEUTRAL_GREY_500,
						cursor: "pointer",
					}}
					onClick={() => {
						props.setCurrentPage(CurrentLoginPage.FORGET_PASSWORD);
					}}
				>
					<u>{translation("ForgetPassword")}</u>
				</div>
			</div>
			<BlueButton
				id="login-button"
				children={
					loggingIn ? (
						<ButtonTextLoadingAnimation
							text={translation("Loading")}
						></ButtonTextLoadingAnimation>
					) : isDisabled ? (
						translation("PleaseWaitSeconds", { time: timer })
					) : (
						translation("Signin")
					)
				}
				size={"flex"}
				disabled={loggingIn || isDisabled}
				onClick={() => {
					handleSubmit();
				}}
			></BlueButton>
		</div>
	);
}
